import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import {
  MFA_IDENTITY_TYPES,
  MfaConfirmationScreen,
  MfaIdentitiesList,
  type MFAIdentityType,
} from '@/entities/mfa-identity';
import { sendTwoFactorIdentityCode } from '@/features/user-settings-2fa';

import {
  OPERATION_MFA_VIEW_TYPE,
  type OperationMFAVerificationOptions,
  type OperationMFAViewState,
  useOperationMFAVerification,
} from '@/features/operation-mfa-verification';
import { useTranslation } from 'react-i18next';
import { Loader } from '@/shared/components';
import type { AxiosError } from 'axios';

export type OperationMFAVerificationRef = {
  startRequest: () => void;
  handleBackToMethodSelection?: () => void;
};

type OperationMFAVerificationProps<
  TParams = unknown,
  TResponse = unknown,
  TError extends AxiosError = AxiosError,
> = OperationMFAVerificationOptions<TParams, TResponse, TError> & {
  ref?: React.ForwardedRef<OperationMFAVerificationRef>;
};

function OperationMFAVerificationComponent<
  TParams = unknown,
  TResponse = unknown,
  TError extends AxiosError = AxiosError,
>(
  props: OperationMFAVerificationOptions<TParams, TResponse, TError>,
  ref: React.ForwardedRef<OperationMFAVerificationRef>
) {
  const { t } = useTranslation();
  const {
    form,
    status,
    viewState,
    selectedIdentityType,
    maskedContact,
    setMaskedContact,
    setIsSendingOTP,
    setError,
    handleMethodSelected,
    handleOTPSubmit,
    startRequest,
    availableMethods,
    handleBackToMethodSelection,
  } = useOperationMFAVerification(props);
  const prevViewStateRef = useRef<OperationMFAViewState | null>(null);
  const prevMaskedContactStateRef = useRef<string | null | undefined>(null);
  const prevSelectedIdentityTypeRef = useRef<MFAIdentityType | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      startRequest,
      handleBackToMethodSelection,
    }),
    [startRequest, handleBackToMethodSelection]
  );

  useEffect(() => {
    if (
      prevViewStateRef.current === viewState &&
      prevMaskedContactStateRef.current === maskedContact &&
      prevSelectedIdentityTypeRef.current === selectedIdentityType
    ) {
      return;
    }

    prevViewStateRef.current = viewState;
    prevMaskedContactStateRef.current = maskedContact;
    prevSelectedIdentityTypeRef.current = selectedIdentityType;

    if (viewState === OPERATION_MFA_VIEW_TYPE.SETUP_REQUIRED) {
      props?.onViewChange?.(null);
    } else if (viewState === OPERATION_MFA_VIEW_TYPE.METHOD_SELECTION) {
      props?.onViewChange?.({
        view: viewState,
        description: t('authPages.mfaMethodsList.description'),
      });
    } else if (viewState === OPERATION_MFA_VIEW_TYPE.CONFIRMATION) {
      if (!selectedIdentityType) {
        props?.onViewChange?.(null);
      }

      let data = null;
      if (selectedIdentityType) {
        let description = '';

        if (selectedIdentityType === MFA_IDENTITY_TYPES.GOOGLE_AUTHENTICATOR) {
          description = t('authPages.identityConfirmationForm.appDescription');
        } else {
          description = maskedContact
            ? `${t('authPages.identityConfirmationForm.description')} ${maskedContact}`
            : '';
        }

        data = {
          view: viewState,
          description,
        };
      }

      props?.onViewChange?.(data);
    }
  }, [viewState, props, maskedContact, selectedIdentityType]);

  const handleSendOtpCode = async (args: {
    identityType: MFAIdentityType;
    state?: string;
  }) => {
    try {
      setIsSendingOTP(true);
      setError(null);
      return await sendTwoFactorIdentityCode(args);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to send OTP code';
      setError(errorMessage);
      throw error;
    } finally {
      setIsSendingOTP(false);
    }
  };

  if (viewState === OPERATION_MFA_VIEW_TYPE.SETUP_REQUIRED) {
    return <Loader />;
  }

  if (viewState === OPERATION_MFA_VIEW_TYPE.METHOD_SELECTION) {
    return (
      <MfaIdentitiesList
        availableIdentities={availableMethods}
        onSelectIdentity={handleMethodSelected}
        isLoading={status.isLoading}
      />
    );
  }

  if (viewState === OPERATION_MFA_VIEW_TYPE.CONFIRMATION) {
    if (!selectedIdentityType) {
      return <Loader />;
    }

    return (
      <MfaConfirmationScreen
        form={form}
        state={form.getValues().twoFA.state}
        identityType={selectedIdentityType}
        mfaCodeFieldName="twoFA.code"
        sendOtpCodeMutationFn={handleSendOtpCode}
        setMaskedContact={setMaskedContact}
        onSubmit={handleOTPSubmit}
        isSubmitLoading={status.isSubmitting}
        submitError={status.errorMessage}
      />
    );
  }

  return <Loader />;
}

const OperationMFAVerificationForwarded = forwardRef(
  OperationMFAVerificationComponent
);

OperationMFAVerificationForwarded.displayName = 'OperationMFAVerification';

export const OperationMFAVerification = OperationMFAVerificationForwarded as <
  TParams = unknown,
  TResponse = unknown,
  TError extends AxiosError = AxiosError,
>(
  props: OperationMFAVerificationProps<TParams, TResponse, TError>
) => React.ReactElement;
